import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getLocalizedString } from '../../Utils/stringLookup';
import TransactionButton from '../Buttons/TransactionButton';
import { useStrings } from '../StringsContext';
import { isCurrency } from '../../Utils/InventoryManagement';
import './TradingInventoryModal.css';
import CloseButton from '../Buttons/CloseButton';

const TradingInventoryModal = ({
  isOpen,
  onClose,
  inventory,
  resourceData,
  amounts,
  handleAmountChange,
  handleAddToSlot,
  getSlotConfig,
  selectedSlotIndex,
  transactionKeyPrefix = 'add-to-trade-slot',
  isRequestMode = false,
  playerMoney = 0
}) => {
  const strings = useStrings();
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  // Every opening starts alphabetical by Item (the modal stays mounted between openings)
  useEffect(() => {
    if (isOpen) { setSortField('name'); setSortDirection('asc'); }
  }, [isOpen]);

  // Sort inventory data
  const sortedInventory = useMemo(() => {
    const filtered = inventory.filter(item => !isCurrency(item.type));
    
    return filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'name':
          aValue = getLocalizedString(a.type, strings).toLowerCase();
          bValue = getLocalizedString(b.type, strings).toLowerCase();
          break;
        case 'quantity':
          aValue = a.quantity;
          bValue = b.quantity;
          break;
        case 'price':
          const aResource = resourceData.find(res => res.type === a.type);
          const bResource = resourceData.find(res => res.type === b.type);
          aValue = aResource?.maxprice || 0;
          bValue = bResource?.maxprice || 0;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [inventory, resourceData, amounts, strings, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '⬆️' : '⬇️';
  };

  if (!isOpen) return null;

  const slotConfig = getSlotConfig(selectedSlotIndex);

  // Rendered through a portal to document.body: the Trade Stall panel that opens this modal is
  // transformed on phones, which would otherwise trap the modal inside the panel's box
  // (docs/ui-conventions.md, Modals).
  return createPortal(
    <div className="inventory-modal wider">
      <CloseButton corner onClick={onClose} />

      {/* Title (Berkshire, the modal-title role) over a Lora sub-header naming the action */}
      <h2 className="modal-title">{strings[200023] || 'Trading Post'}</h2>
      <h3 className="modal-subtitle">{isRequestMode ? (strings[10180] || 'Request Items to Buy') : (strings[160] || 'Add Items to Sell')}</h3>

      <p className="trading-modal-subtitle">
        {slotConfig.maxAmount} {isRequestMode ? strings[10181] : strings[158]}
      </p>
      
      <div className="inventory-modal-container res-rows">
        <div className="inventory-modal-header res-rows-head">
          <table>
            <thead>
              <tr>
                <th onClick={() => handleSort('name')} className="sortable-header">
                  {strings[161]} {getSortIcon('name')}
                </th>
                <th onClick={() => handleSort('quantity')} className="sortable-header">
                  {strings[162]} {getSortIcon('quantity')}
                </th>
                <th onClick={() => handleSort('price')} className="sortable-header">
                  {strings[163]} {getSortIcon('price')}
                </th>
                <th>
                  {isRequestMode ? 'Amount to Request' : strings[164]}
                </th>
                <th></th>
              </tr>
            </thead>
          </table>
        </div>
        
        <div className="inventory-modal-scroll res-rows-body">
          <table>
            <tbody>
              {sortedInventory.map((item) => {
                const resourceDetails = resourceData.find((res) => res.type === item.type);
                const price = resourceDetails?.maxprice || 0;
                const currentAmount = amounts[item.type] || 0;
                const totalCost = currentAmount * price;

                // Calculate max affordable amount in request mode
                const maxAffordable = isRequestMode ? Math.floor(playerMoney / price) : item.quantity;
                const maxAllowed = Math.min(isRequestMode ? maxAffordable : item.quantity, slotConfig.maxAmount);

                return (
                  <tr key={item.type}>
                    <td className="res-name">{resourceDetails?.symbol} {getLocalizedString(item.type, strings)}</td>
                    <td className="res-meta" data-label={strings[162]}>{item.quantity}</td>
                    <td className="res-meta">💰 {price}</td>
                    <td className="res-amount">
                      <div className="amount-input">
                        <button
                          onClick={() =>
                            handleAmountChange(item.type, currentAmount - 1)
                          }
                          disabled={currentAmount <= 0}
                        >
                          -
                        </button>
                        <input
                          type="number"
                          value={currentAmount}
                          onChange={(e) =>
                            handleAmountChange(item.type, parseInt(e.target.value, 10) || 0)
                          }
                        />
                        <button
                          onClick={() =>
                            handleAmountChange(item.type, currentAmount + 1)
                          }
                          disabled={currentAmount >= maxAllowed}
                        >
                          +
                        </button>
                        <button
                          onClick={() =>
                            handleAmountChange(item.type, maxAllowed)
                          }
                          style={{ marginLeft: '4px' }}
                        >
                          {strings[165]}
                        </button>
                      </div>
                    </td>
                    <td className="res-action">
                      <TransactionButton
                        className="add-button"
                        onAction={(transactionId, transactionKey) =>
                          handleAddToSlot(transactionId, transactionKey, item.type)
                        }
                        transactionKey={`${transactionKeyPrefix}-${item.type}`}
                        disabled={
                          isRequestMode
                            ? !(currentAmount > 0 && totalCost <= playerMoney)
                            : !(currentAmount > 0 && currentAmount <= item.quantity)
                        }
                      >
                        {isRequestMode ? 'Request' : strings[166]}
                      </TransactionButton>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  , document.body);
};

export default TradingInventoryModal;